import React, { useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState(1); // 1: send OTP, 2: verify OTP, 3: reset password
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // Step 1: Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await axios.post("/api/v1/auth/forgot-password/send-otp", {
        email,
      });
      if (res.data.success) {
        toast.success(res.data.message);
        setStep(2);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error("Error sending OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await axios.post("/api/v1/auth/forgot-password/verify-otp", {
        email,
        otp,
      });
      if (res.data.success) {
        toast.success(res.data.message);
        setStep(3);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error("Error verifying OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await axios.post("/api/v1/auth/forgot-password", {
        email,
        newPassword,
      });
      if (res.data.success) {
        toast.success("Password reset successful!");
        navigate("/login");
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error("Error resetting password");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (step === 1) handleSendOtp(e);
    else if (step === 2) handleVerifyOtp(e);
    else handleResetPassword(e);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative overflow-hidden">
      {/* LEFT SIDE - BLACK WITH CURVED EDGE */}
      <div className="w-full lg:w-1/2 bg-black relative flex items-center justify-center min-h-[30vh] lg:min-h-screen py-8 lg:py-0">
        {/* Logo Section */}
        <div className="text-white z-10 text-center px-6">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl"
            style={{ fontFamily: "'TAN PEARL', serif" }}
          >
            Russh Berry
          </h1>
          <p className="text-base sm:text-lg lg:text-xl mt-3 lg:mt-4 text-gray-400">
            Where Luxury Meets Elegance
          </p>
        </div>

        {/* Curved SVG Divider - Desktop Only */}
        <div className="hidden lg:block absolute right-0 top-0 h-full w-64 overflow-hidden">
          <svg
            className="absolute right-0 h-full"
            viewBox="0 0 400 800"
            preserveAspectRatio="none"
            style={{ transform: "translateX(50%)" }}
          >
            <path d="M 0 0 Q 200 400 0 800 L 400 800 L 400 0 Z" fill="white" />
          </svg>
        </div>

        {/* Mobile/Tablet Wave Divider */}
        <div className="lg:hidden absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg
            className="relative block w-full h-12 sm:h-16"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 C300,100 900,100 1200,0 L1200,120 L0,120 Z"
              fill="white"
            />
          </svg>
        </div>
      </div>

      {/* RIGHT SIDE - WHITE WITH FORM */}
      <div className="w-full lg:w-1/2 bg-white flex items-center justify-center px-6 sm:px-12 lg:px-16 py-12 lg:py-0">
        <div className="w-full max-w-md">
          {/* Header with Step Indicator */}
          <div className="mb-8 lg:mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              {step === 1 && "FORGOT PASSWORD"}
              {step === 2 && "VERIFY OTP"}
              {step === 3 && "RESET PASSWORD"}
            </h2>
            <p className="text-sm text-gray-600">
              {step === 1 && "Enter your email to receive a verification code"}
              {step === 2 && "Enter the OTP sent to your email"}
              {step === 3 && "Create a new password for your account"}
            </p>

            {/* Progress Steps */}
            <div className="flex items-center gap-2 mt-6">
              <div
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  step >= 1 ? "bg-black" : "bg-gray-300"
                }`}
              ></div>
              <div
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  step >= 2 ? "bg-black" : "bg-gray-300"
                }`}
              ></div>
              <div
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  step >= 3 ? "bg-black" : "bg-gray-300"
                }`}
              ></div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            {/* Email Input */}
            <div className="relative">
              <div className="absolute left-0 top-1/2 -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                required
                disabled={step !== 1}
                className="w-full pl-8 pr-4 py-3 sm:py-3.5 border-b-2 border-gray-300 focus:border-black outline-none transition-colors bg-transparent text-base placeholder:text-gray-400 disabled:opacity-50"
              />
            </div>

            {/* OTP Input */}
            {step >= 2 && (
              <div className="relative">
                <div className="absolute left-0 top-1/2 -translate-y-1/2">
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter 6-digit OTP"
                  required
                  maxLength={6}
                  className="w-full pl-8 pr-4 py-3 sm:py-3.5 border-b-2 border-gray-300 focus:border-black outline-none transition-colors bg-transparent text-base placeholder:text-gray-400 tracking-widest"
                />
                {step === 2 && (
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setOtp("");
                    }}
                    className="text-xs text-gray-600 hover:text-black mt-2 underline"
                  >
                    Didn't receive OTP? Resend
                  </button>
                )}
              </div>
            )}

            {/* New Password Input */}
            {step === 3 && (
              <div className="relative">
                <div className="absolute left-0 top-1/2 -translate-y-1/2">
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New Password"
                  required
                  minLength={6}
                  className="w-full pl-8 pr-4 py-3 sm:py-3.5 border-b-2 border-gray-300 focus:border-black outline-none transition-colors bg-transparent text-base placeholder:text-gray-400"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Password must be at least 6 characters
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-3.5 sm:py-4 rounded-none font-semibold text-base sm:text-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  PROCESSING...
                </span>
              ) : (
                <>
                  {step === 1 && "SEND OTP"}
                  {step === 2 && "VERIFY OTP"}
                  {step === 3 && "RESET PASSWORD"}
                </>
              )}
            </button>

            {/* Back to Login */}
            <div className="relative py-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">OR</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full bg-white border-2 border-black text-black py-3.5 sm:py-4 rounded-none font-semibold text-base sm:text-lg hover:bg-gray-50 transition-colors"
            >
              BACK TO LOGIN
            </button>
          </form>

          <p className="text-xs sm:text-sm text-gray-500 text-center mt-6 leading-relaxed">
            SECURED BY WEBZARK TECHNOLOGIES
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
